// v10/src/components/admin/lifespan/EditComponentModal.js
//
// Modal "Editar componente" del módulo Vida límite (A6.1) — Tarea 2.
// PUT /api/aircraft-components/:id
//
// Reglas:
//   • source==='catalog' → name/category/manufacturer NO editables (vienen del catálogo)
//   • source==='custom'  → name/category/manufacturer SÍ editables
//   • Siempre editables: serial_number, tbo_hours, tbo_calendar_months,
//     installed_at_date, installed_at_horometer, notes

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Save, Edit3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import componentLifespanService from '../../../services/componentLifespanService';

const CATEGORY_KEYS = [
  'engine', 'propeller', 'magneto', 'elt_battery', 'fire_extinguisher',
  'seatbelt', 'pitot_tube', 'spark_plug', 'oil_filter', 'other',
];

const todayIso = () => new Date().toISOString().split('T')[0];

// Convierte un timestamp ISO o YYYY-MM-DD al formato YYYY-MM-DD aceptado por <input type="date">.
const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const EditComponentModal = ({ component, onSuccess, onClose, darkMode }) => {
  const { t } = useTranslation('admin');

  const isCustom = component?.source === 'custom';

  const [form, setForm] = useState({
    name: component?.name || '',
    category: component?.category || '',
    manufacturer: component?.manufacturer || '',
    serial_number: component?.serial_number || '',
    tbo_hours: component?.tbo_hours != null ? String(component.tbo_hours) : '',
    tbo_calendar_months: component?.tbo_calendar_months != null ? String(component.tbo_calendar_months) : '',
    installed_at_date: toDateInput(component?.installed_at_date),
    installed_at_horometer: component?.installed_at_horometer != null ? String(component.installed_at_horometer) : '',
    notes: component?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validation = useMemo(() => {
    const errors = [];

    if (isCustom) {
      if (!form.name.trim()) errors.push(t('lifespan_validation_name_required'));
      if (!form.category) errors.push(t('lifespan_validation_category_required'));
    }

    const tboH = form.tbo_hours === '' ? null : Number(form.tbo_hours);
    const tboM = form.tbo_calendar_months === '' ? null : Number(form.tbo_calendar_months);
    const hasH = tboH !== null && Number.isFinite(tboH) && tboH > 0;
    const hasM = tboM !== null && Number.isFinite(tboM) && tboM > 0;

    if (tboH !== null && Number.isFinite(tboH) && tboH <= 0) {
      errors.push(t('lifespan_validation_tbo_hours_positive'));
    }
    if (tboM !== null && Number.isFinite(tboM) && tboM <= 0) {
      errors.push(t('lifespan_validation_tbo_months_positive'));
    }
    if (!hasH && !hasM) {
      errors.push(t('lifespan_validation_tbo_required'));
    }

    if (!form.installed_at_date) {
      errors.push(t('lifespan_validation_installed_date_required'));
    } else if (form.installed_at_date > todayIso()) {
      errors.push(t('lifespan_validation_installed_date_future'));
    }

    if (form.installed_at_horometer === '') {
      errors.push(t('lifespan_validation_installed_horometer_required'));
    } else {
      const h = Number(form.installed_at_horometer);
      if (!Number.isFinite(h) || h < 0) {
        errors.push(t('lifespan_validation_installed_horometer_negative'));
      }
    }

    return { errors, ok: errors.length === 0 };
  }, [form, isCustom, t]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validation.ok || loading || !component?.id) return;

    const payload = {
      serial_number: form.serial_number.trim() || null,
      installed_at_date: form.installed_at_date,
      installed_at_horometer: Number(form.installed_at_horometer),
      notes: form.notes.trim() || null,
    };
    if (form.tbo_hours !== '') payload.tbo_hours = Number(form.tbo_hours);
    if (form.tbo_calendar_months !== '') payload.tbo_calendar_months = Number(form.tbo_calendar_months);

    if (isCustom) {
      payload.name = form.name.trim();
      payload.category = form.category;
      payload.manufacturer = form.manufacturer.trim() || null;
    }

    setLoading(true); setError('');
    try {
      await componentLifespanService.updateComponent(component.id, payload);
      if (!isMounted.current) return;
      onSuccess?.();
      onClose();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.response?.data?.message || t('lifespan_validation_network_error'));
      setLoading(false);
    }
  };

  // Estilos
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const lockedStyle = {
    ...inputStyle,
    opacity: 0.55,
    cursor: 'not-allowed',
    background: darkMode ? 'rgba(255,255,255,0.02)' : '#f1f5f9',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={loading ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 600, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(79,70,229,0.3)',
              }}
            >
              <Edit3 style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('component_edit_modal_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('component_edit_modal_subtitle', { name: component?.name || '' })}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={loading}
            aria-label="close"
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: loading ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Nombre */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('component_create_modal_label_name')}</p>
            <input
              type="text" maxLength={150}
              style={isCustom ? inputStyle : lockedStyle}
              value={form.name}
              onChange={isCustom ? (e) => setField('name', e.target.value) : undefined}
              readOnly={!isCustom}
              disabled={!isCustom}
            />
            {!isCustom && (
              <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                {t('component_edit_modal_locked_field_hint')}
              </p>
            )}
          </div>

          {/* Categoría */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('component_create_modal_label_category')}</p>
            {isCustom ? (
              <select
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
              >
                <option value="">{t('component_create_modal_placeholder_category')}</option>
                {CATEGORY_KEYS.map((k) => (
                  <option key={k} value={k}>{t(`component_category_${k}`)}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                style={lockedStyle}
                value={form.category ? t(`component_category_${form.category}`, { defaultValue: form.category }) : ''}
                readOnly
                disabled
              />
            )}
          </div>

          {/* Manufacturer */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('component_create_modal_label_manufacturer')}</p>
            <input
              type="text" maxLength={100}
              style={isCustom ? inputStyle : lockedStyle}
              value={form.manufacturer}
              onChange={isCustom ? (e) => setField('manufacturer', e.target.value) : undefined}
              readOnly={!isCustom}
              disabled={!isCustom}
            />
          </div>

          {/* Serial */}
          <div style={{ marginBottom: 14 }}>
            <p style={labelStyle}>{t('component_create_modal_label_serial_number')}</p>
            <input
              type="text" maxLength={100} style={inputStyle}
              value={form.serial_number}
              onChange={(e) => setField('serial_number', e.target.value)}
              placeholder={t('component_create_modal_placeholder_serial_number')}
            />
          </div>

          {/* TBOs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <p style={labelStyle}>{t('component_create_modal_label_tbo_hours')}</p>
              <input
                type="number" min="0" step="0.1" style={inputStyle}
                value={form.tbo_hours}
                onChange={(e) => setField('tbo_hours', e.target.value)}
                placeholder={t('component_create_modal_placeholder_tbo_hours')}
              />
            </div>
            <div>
              <p style={labelStyle}>{t('component_create_modal_label_tbo_months')}</p>
              <input
                type="number" min="0" step="1" style={inputStyle}
                value={form.tbo_calendar_months}
                onChange={(e) => setField('tbo_calendar_months', e.target.value)}
                placeholder={t('component_create_modal_placeholder_tbo_months')}
              />
            </div>
          </div>
          <p style={{ margin: '0 0 14px', fontSize: 11, color: fgFaint }}>
            {t('component_create_modal_hint_tbo_at_least_one')}
          </p>

          {/* Instalación */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <p style={labelStyle}>{t('component_create_modal_label_installed_date')}</p>
              <input
                type="date" max={todayIso()}
                style={{ ...inputStyle, cursor: 'text' }}
                value={form.installed_at_date}
                onChange={(e) => setField('installed_at_date', e.target.value)}
              />
            </div>
            <div>
              <p style={labelStyle}>{t('component_create_modal_label_installed_horometer')}</p>
              <input
                type="number" min="0" step="0.1" style={inputStyle}
                value={form.installed_at_horometer}
                onChange={(e) => setField('installed_at_horometer', e.target.value)}
                placeholder={t('component_create_modal_placeholder_installed_horometer')}
              />
            </div>
          </div>

          {/* Notas */}
          <div style={{ marginBottom: 16 }}>
            <p style={labelStyle}>{t('component_create_modal_label_notes')}</p>
            <textarea
              rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60, cursor: 'text' }}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder={t('component_create_modal_placeholder_notes')}
            />
          </div>

          {validation.errors.length > 0 && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              {validation.errors.map((e, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {e}
                </p>
              ))}
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                marginBottom: 12,
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={loading || !validation.ok}
              whileHover={!loading && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!loading && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: loading || !validation.ok ? 'not-allowed' : 'pointer',
                background: loading || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#4f46e5,#6366f1)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {loading
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <Save style={{ width: 16, height: 16 }} />
              }
              {loading ? t('component_edit_modal_btn_saving') : t('component_edit_modal_btn_submit')}
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={loading}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted,
                fontFamily: 'inherit',
              }}
            >
              {t('lifespan_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default EditComponentModal;
